import PageLoader from '@/common/service/flow/page-loader';
import { buildAccountBaseService } from '@/common/service/base-paid';
import { loadSelectByPersist, filterCapacity } from '@/common/service/plan-data-mapper';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import store from '@/store/index';
import {
  checkZeSaasCapacity,
  freeTrialUpgrade,
  getPPARemainingCredits,
  updatePlans,
  validateEditBulkLicense,
} from '@/apis';
import _, { get } from 'lodash';
import { pollingEventStatus } from '@/common/polling';
import { filterDisabledPlansWithSubscription } from '@/flows/ADD';
import {
  gotoSuccessPage,
  gotoWiseSuccessPage,
  upgradeSuccessPage,
  wiseUpgradeSuccessPage,
} from '@/common/service/common';
import { alertWithPendingCoupon } from '@/common/service/validate/coupon';
import { isEmptyVal } from '@/utils';
import {
  addToCartByCategoryAndBillingCycle,
  getCurrentPlanCycleDepFirstPlanForInit, planQueryParser,
} from '@/common/service/flow/utils';
import {
  configCapacityTypes,
  BILLING_CYCLE,
  BillingCycleEnum,
  BASE_PLAN_SUB_TYPES,
  planTypes,
  ZOOM_EVENTS_SUB_TYPES,
  MRS_STATUS_TYPE_MAP,
  ENUM_UPGRADE_TYPE,
  updateTypeMap,
  ZOOM_MEETING_EDU_SCH_CMP_MIN_LICENSE,
  ZOOM_MEETING_EDU_PLUS_MIN_LICENSE,
  VIEWS,
  APM_PAYMENT_METHODS,
} from '@/common/constants';
import { getBasePlanInfo } from '@/common/product';
import { updatePlansCheckout, UpdateTypes, getPaymentInfo, addParamsIfApm, addParamsIfNeedUseVerification } from '@/common/service/checkout-utils';
import Flow from '@/flows/flow';
import { capacityConfig } from '@/apis';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { getAssignLicenseResult } from '@/common/service/validate/assign-license';
import BillingMessageBox from '@/components/common/message-box';
import { translation } from '@/plugins/i18n';
import { formJump } from '@/common/utils';
import { APM, WISE } from '@/views/checkout/payment/helper/constant';
import { getMeetingPlanByCategory } from '@/plans/ZOOM_MEETING/common/utils';
import { getBundlePbxPlan, getProp2BundleUpgradePlan, getZs2ZeUpgradePlan, getEdu2EscUpgradePlanQuantity } from '@/plans/ZOOM_ONE_BUNDLE/utils';
import {
  getMeeting2BizPlusUpdateForm,
  getPro2ProPlusUpdateForm,
  getProPlus2BizPlusUpdateForm,
  checkAudioChange,
  getEdu2EduPlusUpdateForm,
  getEdu2EscUpdateForm
} from '../utils.js';
import { filterEventPlansForAdd2Cart } from '@/plans/EVENT_SAAS/helper/filter';
import { loadDefaultPlansByPreloadCouponForUpgrade } from '@/views/config/coupon/helper/preload';
import { parseConsumptionCode } from '@/plans/EVENT_SAAS/helper/parse';
import { dateTimeFormat } from '@/utils/dateformat';
import { checkMRSChange } from '@/utils/validator';
import { plansof } from '@/utils/chain';
import { showCouponTermsMessage } from '@/views/config/coupon/helper/utils';
import { ONLINE_NOT_SUPPORT_BILLING_CYCLE } from '@/common/service/non-standard-billing-cycle-utils';
import MessageBox from '@zoom/zoom-ui/src/components/message-box/message-box';
import EventTracking from '@/aspects/trackings';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils.js';
import { getOrderSummaryFields } from '@/flows/utils';
import Pbx2prop from './pbx2prop.js';

const loadDefaultSelectByQueryForPro2Biz = (boughtPlans = []) => {
  return (planList = []) => {
    const { targetCategory = [], planType } = pageConfig?.editConfig || {};

    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    const boughtBasePlan = boughtPlans?.find((p) => p.type === planType);
    if (!hasSelected) {
      let { billing_cycle } = boughtBasePlan || {};

      // biz/pro mouthly to edu annual, edu meeting only has annual cycle
      if (targetCategory === BASE_PLAN_SUB_TYPES.EDUCATION) {
        billing_cycle = BILLING_CYCLE.ANNUAL;
      }

      return planList.map((plan) => {
        const isMatchTargetPlan = plan.category === targetCategory && plan.billing_cycle === billing_cycle;

        return isMatchTargetPlan
          ? {
              ...plan,
              cartInfo: {
                ...plan.cartInfo,
                selected_number: Math.max(plan.min, boughtBasePlan?.cartInfo.selected_number || 1),
              },
            }
          : plan;
      });
    }

    return planList;
  };
};

const loadDefaultSelectByQueryForUpgrade = (planList = []) => {
  const { plan } = pageConfig?.editConfig || {};

  const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
  if (hasSelected) {
    return planList;
  }

  addToCartByCategoryAndBillingCycle(planList, getPlanTypeByCategory(plan), plan, BILLING_CYCLE.ANNUAL);

  return planList;
};

const initShoppingCartForPro2Biz = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  const meetingPlan = plansof(boughtPlans).type(planTypes.ZOOM_MEETING).category(BASE_PLAN_SUB_TYPES.PRO).get(0);

  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate(
    'loadDefaultPlansByPreloadCouponForUpgrade',
    loadDefaultPlansByPreloadCouponForUpgrade(boughtPlans, meetingPlan),
  );
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForPro2Biz(boughtPlans));
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const initShoppingCartForUpgrade = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();

  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForUpgrade);
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const checkOutForPro2BizSubmit = async (submitParams) => {
  const preview = submitParams?.preview ?? false;
  const previewActions = submitParams?.previewActions ?? [];
  const { apmInfo, paymentMethodInfo, boughtPlans, orderDataPayload } = store.getters;
  const pro = getMeetingPlanByCategory(boughtPlans, BASE_PLAN_SUB_TYPES.PRO);
  let oldPlanCode = pro?.plan_code;
  // * biz to edu
  if (!oldPlanCode && window.$context.is.edu && window.$context.is.pro2biz) {
    let biz = getMeetingPlanByCategory(boughtPlans, BASE_PLAN_SUB_TYPES.BIZ);
    oldPlanCode = biz?.plan_code;
  }
  const planInfo = get(store.getters?.selectedPlanListPayLoad, '[0]', {});
  let extraKeys = {};
  if (ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(pro?.billing_cycle)) {
    Object.assign(extraKeys, { planType: planInfo?.planType, oldPlanType: pro?.plan_type });
  }
  const planList = [{ ...planInfo, oldPlanCode, ...extraKeys }];
  const couponData = orderDataPayload?.coupon;
  let { paymentResponse } = paymentMethodInfo;
  let params = {
    planList,
    indiaCustomerNonINR: false,
    actionType: UpdateTypes.PRO_TO_BIZ,
    paymentResponse,
    couponData,
    preview: preview,
    previewActions,
    orderSummary: {
      orderSummaryFields: getOrderSummaryFields(store.getters),
    },
  };
  if (window.$context.is.edu && window.$context.is.pro2biz) {
    params = checkAudioChange(store.getters, params);
  }
  addParamsIfApm(params);
  const result = await updatePlans(params);
  const options = preview ? { previewKey: result?.result?.previewKey, rps: true } : { rps: true };
  const eventResult = await pollingEventStatus(options);
  if (apmInfo.isApm && !preview) {
    const successParams = {
      integrationType: APM,
      apmMethodName: apmInfo.apmPaymentMethodName,
    };
    apmInfo.apmPaymentMethodName === WISE
      ? wiseUpgradeSuccessPage({})
      : apmInfo.apmPaymentMethodName === APM_PAYMENT_METHODS.P24 || apmInfo.apmPaymentMethodName === APM_PAYMENT_METHODS.KLARNA
      ? upgradeSuccessPage()
      : gotoSuccessPage(successParams);
    return {
      eventResult,
    };
  }

  if (!preview) {
    gotoSuccessPage();
  }

  return {
    eventResult,
  };
};

const checkOutForUpgrade = async (submitParams) => {
  const getters = store.getters;
  const { planType } = pageConfig.editConfig;
  const currentSelectedPlan = getters.getSelectedPlans.find((item) => item.type === planType);
  const boughtBasePlan = getBasePlanInfo(getters.boughtPlans);
  const oldPlanCode = get(boughtBasePlan, 'plans.0.plan_code', '');
  const actionType = UpdateTypes.ZONE_BASIC_PLUS_UPGRADE_TO_MEETING;
  const planList = [
    {
      quantity: currentSelectedPlan.cartInfo.selected_number,
      planCode: currentSelectedPlan.plan_code,
      oldPlanCode,
    },
  ];
  return updatePlansCheckout(submitParams, planList, actionType);
};

const loadDefaultSelectByQueryForTrial2Paid = (trialPlans = [], boughtPlans = []) => {
  return (planList = []) => {
    const { planType, category } = pageConfig?.editConfig || {};

    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    const basePlan = trialPlans?.find((p) => p.type === planType);

    if (!hasSelected) {
      const { billing_cycle, cartInfo = {} } = basePlan || {};
      let period = billing_cycle;

      const currentPlanCycleDepFirstPlan = getCurrentPlanCycleDepFirstPlanForInit(trialPlans, boughtPlans, planType);

      // compare depend base plan billing cycle with url query period(billing cycle) parameter
      if (currentPlanCycleDepFirstPlan && BillingCycleEnum[period] > currentPlanCycleDepFirstPlan?.cycleValue) {
        period = currentPlanCycleDepFirstPlan?.billing_cycle;
      }
      addToCartByCategoryAndBillingCycle(planList, planType, category, period, cartInfo.selected_number || 1);
    }

    return planList;
  };
};

const filterPlanList4BoughtPlans = (boughtPlans = [], editPlanType) => {
  return (planList = []) => {
    // check ze/zs plan list should align base plan billing cycle
    if ([planTypes.EVENT_SAAS, planTypes.EVENT_SAAS_CORE].includes(editPlanType)) {
      return filterEventPlansForAdd2Cart(planList, boughtPlans);
    }
    return planList;
  };
};

const initShoppingCartTrial2Paid = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  // ZOOM-908441: if no free trial plan needs to be upgrade, redirect to billing
  if(freeTrialPlans.length === 0) {
    location.replace('/billing');
  }
  const currentEditPlan = _.find(freeTrialPlans, (plan) => plan.plan_code === pageConfig.oldPlanCode) || {};
  const assignResult = await getAssignLicenseResult(currentEditPlan);
  // Filter capacity
  const isCapacityPlan = _.includes(configCapacityTypes, currentEditPlan.type);
  const { result: capacityList } = isCapacityPlan
    ? await capacityConfig({ flowType: flowTypes.add2cart, planKey: currentEditPlan.type })
    : {};

  const loader = new PageLoader(planMap);
  loader.decorate(
    'filterDisabledPlansWithSubscription',
    filterDisabledPlansWithSubscription(allPlanMap, subscriptions),
  );
  loader.decorate(
    'filterPlanList4BoughtPlans',
    filterPlanList4BoughtPlans(boughtPlans, currentEditPlan.type || pageConfig?.editConfig?.planType),
  );
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForTrial2Paid(freeTrialPlans, boughtPlans));
  isCapacityPlan && loader.decorate('filterCapacity', filterCapacity(capacityList));
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const initShoppingCartForMeeting2Bundle = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  const boughtMeetingCategory = _.chain(boughtPlans)
    .find((p) => p.type === planTypes.ZOOM_MEETING)
    .get('category')
    .value();

  // pro/biz meeting plan
  const meetingPlan = getMeetingPlanByCategory(boughtPlans, boughtMeetingCategory);
  const assignResult = await getAssignLicenseResult(meetingPlan);
  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate(
    'loadDefaultPlansByPreloadCouponForUpgrade',
    loadDefaultPlansByPreloadCouponForUpgrade(boughtPlans, meetingPlan),
  );
  loader.decorate(
    'loadDefaultSelectByQuery',
    loadDefaultSelectByQueryForMeeting2Bundle(boughtPlans, assignResult, boughtMeetingCategory),
  );
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const loadDefaultSelectByQueryForMeeting2Bundle = (boughtPlans = [], assignResult = {}, meetingCategory) => {
  return (planList = []) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (!hasSelected) {
      const { category_codes = [] } = planQueryParser(pageConfig.from);
      const planInfo = getBundlePbxPlan({
        boughtPlans,
        planMapFlat: planList,
        assignInfo: assignResult,
        category: meetingCategory ?? BASE_PLAN_SUB_TYPES.BIZ,
        defaultCategory: category_codes[0]
      });

      return planList.map((plan) => {
        let selected_number = planInfo[plan.type]?.planCode === plan.plan_code ? planInfo[plan.type].quantity : 0;
        return {
          ...plan,
          cartInfo: {
            ...plan.cartInfo,
            selected_number: selected_number > 0 ? selected_number : plan.cartInfo.selected_number,
          },
        };
      });
    }

    return planList;
  };
};

const checkOutForMeeting2BundleSubmit = async (submitParams) => {
  const getters = store.getters;
  const updateForm = getMeeting2BizPlusUpdateForm(getters);
  const couponData = getters.orderDataPayload.coupon;
  return updatePlansCheckout(
    submitParams,
    updateForm.planList,
    updateForm.actionType,
    couponData,
    updateForm.extensionUpdateData,
  );
};

const initShoppingCartForPro2Prop = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  const meetingPlan = getMeetingPlanByCategory(boughtPlans, BASE_PLAN_SUB_TYPES.PRO);
  const assignResult = await getAssignLicenseResult(meetingPlan);
  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate(
    'loadDefaultPlansByPreloadCouponForUpgrade',
    loadDefaultPlansByPreloadCouponForUpgrade(boughtPlans, meetingPlan),
  );
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForPro2Prop(boughtPlans, assignResult));
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const loadDefaultSelectByQueryForPro2Prop = (boughtPlans = [], assignResult = {}) => {
  return (planList = []) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (!hasSelected) {
      const meetingPlan = getMeetingPlanByCategory(boughtPlans, BASE_PLAN_SUB_TYPES.PRO);
      const planInfo = getBundlePbxPlan({
        boughtPlans,
        planMapFlat: planList,
        assignInfo: assignResult,
        category: BASE_PLAN_SUB_TYPES.PRO,
        planType: planTypes.ZONE_PRO_PLUS,
        quantity: meetingPlan?.cartInfo?.selected_number ?? 1,
      });

      return planList.map((plan) => {
        let selected_number = planInfo[plan.type]?.planCode === plan.plan_code ? planInfo[plan.type].quantity : 0;
        return {
          ...plan,
          cartInfo: {
            ...plan.cartInfo,
            selected_number: selected_number > 0 ? selected_number : plan.cartInfo.selected_number,
          },
        };
      });
    }

    return planList;
  };
};

const checkOutForPro2PropSubmit = async (submitParams) => {
  const getters = store.getters;
  const updateForm = getPro2ProPlusUpdateForm(getters);
  const couponData = getters.orderDataPayload.coupon;
  return updatePlansCheckout(
    submitParams,
    updateForm.planList,
    updateForm.actionType,
    couponData,
    updateForm.extensionUpdateData,
  );
};

const initShoppingCartForProp2Bundle = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  // biz plan
  const proplus = boughtPlans.find((p) => p.type === planTypes.ZONE_PRO_PLUS);
  const assignResult = await getAssignLicenseResult(proplus);
  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate(
    'loadDefaultPlansByPreloadCouponForUpgrade',
    loadDefaultPlansByPreloadCouponForUpgrade(boughtPlans, proplus),
  );
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForProp2Bundle(boughtPlans));
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const initShoppingCartForZs2Ze = async (state) => {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  // biz plan
  const zsPlan = boughtPlans.find((p) => p.plan_code === pageConfig?.oldPlanCode);
  const assignResult = await getAssignLicenseResult(zsPlan);
  const { result: capacityList } = await capacityConfig({
    flowType: flowTypes.zs2ze,
    planKey: planTypes.EVENT_SAAS,
  });

  const loader = new PageLoader(planMap);
  if (![5000, 10000].includes(zsPlan?.capacity)) {
    loader.decorate(
      'filterDisabledPlansWithSubscription',
      filterDisabledPlansWithSubscription(allPlanMap, subscriptions),
    );
  }
  loader.decorate('filterPlanList4BoughtPlans', filterPlanList4BoughtPlans(boughtPlans, planTypes.EVENT_SAAS));
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('filterCapacity', filterCapacity(capacityList));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQueryForZs2Ze(boughtPlans));
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

const loadDefaultSelectByQueryForProp2Bundle = (boughtPlans = [], assignResult = {}) => {
  return (planList = []) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (!hasSelected) {
      const planInfo = getProp2BundleUpgradePlan({
        boughtPlans,
        planMapFlat: planList,
        assignInfo: assignResult,
      });

      return planList.map((plan) => {
        let selected_number = planInfo[plan.type]?.planCode === plan.plan_code ? planInfo[plan.type].quantity : 0;
        return {
          ...plan,
          cartInfo: {
            ...plan.cartInfo,
            selected_number: selected_number > 0 ? selected_number : plan.cartInfo.selected_number,
          },
        };
      });
    }

    return planList;
  };
};

const loadDefaultSelectByQueryForZs2Ze = (boughtPlans = [], assignResult = {}) => {
  return (planList = []) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (!hasSelected) {
      const planInfo = getZs2ZeUpgradePlan({
        boughtPlans,
        planMapFlat: planList,
        assignInfo: assignResult,
      });

      return planList.map((plan) => {
        let selected_number = planInfo[plan.type]?.planCode === plan.plan_code ? planInfo[plan.type].quantity : 0;
        return {
          ...plan,
          cartInfo: {
            ...plan.cartInfo,
            selected_number: selected_number > 0 ? selected_number : plan.cartInfo.selected_number,
          },
        };
      });
    }

    return planList;
  };
};

const checkOutForProp2BundleSubmit = async (submitParams) => {
  const getters = store.getters;
  const updateForm = getProPlus2BizPlusUpdateForm(getters);
  const couponData = getters.orderDataPayload.coupon;
  return updatePlansCheckout(
    submitParams,
    updateForm.planList,
    updateForm.actionType,
    couponData,
    updateForm.extensionUpdateData,
  );
};

const checkOutForTrial2Paid = async (submitParams) => {
  const { planList } = store.getters?.orderDataPayload;
  // Only 1 free trial plan upgrade to paid
  let upgradePlanIndex = 0; // Just pick the first one as the upgrade one. see the reason below.
  let addList = [];
  // More than 1 plan : upgrade to paid plan + other add flow plans
  if(store.getters?.selectedPlanListPayLoad.length > 1 && planList.length > 1) {
    // Just slice the 0 one, because if user changed the capacity, we don't know which one is the upgrade one e.g, oldPlanCode = webinar500_yearly, now user changed to webinar1000_monthly
    const upgradeOne = store.getters?.selectedPlanListPayLoad[upgradePlanIndex];
    // filter the other add flow plans e.g, oldPlanCode = webinar500_yearly, now user add webinar1000, webinar2000, webinar5000...
    addList = planList.filter(orderData => upgradeOne.planCode !== orderData.planCode)
  }
  // Just pick the first one as the upgrade one.
  let params = get(store.getters?.selectedPlanListPayLoad, `[${upgradePlanIndex}]`, {});

  const getters = store.getters;

  const { couponCode = '' } = getters.couponState;
  const { campaign_code: campaignCode = '', trial_code: trialCode = '' } = pageConfig || {};

  const preview = submitParams?.preview ?? false;
  const previewActions = submitParams?.previewActions ?? [];
  params = {
    upgradeList: [
      {
        oldPlanCode: pageConfig.oldPlanCode,
        planCode: params.planCode,
        quantity: params.quantity,
      },
    ],
    addList,
    coupon: { fromCampaignLink: getters.fromCampaignLink, code: couponCode },
    // FREE TRIAL - online free trial upgrade need 2 new params
    ...(campaignCode ? { campaignCode } : {}),
    ...(trialCode ? { trialCode } : {}),
    ...getPaymentInfo(getters),
    preview,
    previewActions,
    orderSummary: {
      orderSummaryFields: getOrderSummaryFields(store.getters),
    },
  };

  if (!!campaignCode && !!trialCode) {
    params.bizType = ENUM_UPGRADE_TYPE.ONLINE_FREE_TRIAL_UPGRADE; // online free trial upgrade, could be more enum type in the future
  }

  params = { ...params, indiaCustomerNonINR: false };
  params.extension = addParamsIfNeedUseVerification(params.extension)
  addParamsIfApm(params);

  const result = await freeTrialUpgrade(params);
  const options = preview ? { previewKey: result?.result?.previewKey, rps: true } : { rps: true };
  const eventResult = await pollingEventStatus(options);

  const { apmInfo } = store.getters;
  if (apmInfo.isApm) {
    const successParams = {
      integrationType: APM,
      apmMethodName: apmInfo.apmPaymentMethodName,
    };
    if (!preview) {
      apmInfo.apmPaymentMethodName === WISE
        ? gotoWiseSuccessPage({ wiseStateValue: apmInfo.wiseStateValue })
        : gotoSuccessPage(successParams);
    }
    return {
      eventResult,
    };
  }

  if (!preview) {
    upgradeSuccessPage(undefined, undefined, 2000, getters?.currentEditPlan?.type);
  }
  return {
    eventResult,
  };
};

const resolveView = () => VIEWS.COMPOUND;

const preCheckForUpgrade = async () => {
  const { hasPendingCoupon, pendingCouponEffectiveDate } = store.getters.basicConfig;

  if (hasPendingCoupon && !isEmptyVal(pendingCouponEffectiveDate)) {
    return alertWithPendingCoupon();
  }

  await showCouponTermsMessage(store.getters.tncMessage);

  return Promise.resolve();
};

const checkAssignLicenseForBulkLicenseAccount = async (currentEditPlan, currentSelectedPlan) => {
  if (!currentEditPlan || !currentSelectedPlan) return Promise.resolve();

  const oldPlanCode = get(currentEditPlan, 'plan_code', '');
  const oldPlanHost = get(currentEditPlan, 'cartInfo.selected_number', 0);
  const currentPlanHost = get(currentSelectedPlan, 'cartInfo.selected_number', 0);

  if (currentPlanHost && oldPlanHost && currentPlanHost < oldPlanHost) {
    try {
      await validateEditBulkLicense({
        planCode: oldPlanCode,
        oldQty: oldPlanHost,
        newQty: currentPlanHost,
      });
      return Promise.resolve();
    } catch (e) {
      MessageBox.alert(e.message, translation('billing.buyItNow.cant_continue'), {
        dangerouslyUseHTMLString: true,
      });

      return Promise.reject();
    }
  }
  return Promise.resolve();
};

const checkAssignLicenseForFree2Paid = async () => {
  const { assignInfo, getSelectedPlans, freeTrialPlans, basicConfig } = store.getters;
  const currentEditPlan = _.find(freeTrialPlans, (plan) => plan.plan_code === pageConfig.oldPlanCode) || {};
  const currentSelectedPlan = getSelectedPlans.find((item) => item.type === currentEditPlan.type);
  const currentAssignedLicense = _.get(assignInfo, [pageConfig.oldPlanCode, 'assignLicenseQuantity'], 0);
  const isBulkLicenseAccount = basicConfig?.isBulkLicenseAccount;

  const bulkCheckSupportTypes = [planTypes.ZOOM_SCHEDULER];
  if (isBulkLicenseAccount && bulkCheckSupportTypes.includes(currentEditPlan.type)) {
    return checkAssignLicenseForBulkLicenseAccount(currentEditPlan, currentSelectedPlan);
  } else if (
    currentSelectedPlan &&
    currentAssignedLicense &&
    currentSelectedPlan.cartInfo.selected_number < currentAssignedLicense
  ) {
    const args = [currentAssignedLicense, currentAssignedLicense - currentSelectedPlan.cartInfo.selected_number];
    if (window.newBillingConfig.isPaid) {
      return BillingMessageBox.confirm(
        translation('billing.planform.zoom_meeting.less_than_assigned_number_tips', args),
        translation('billing.cancel_title_sorry_to_see_you_go'),
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: translation('billing.success.go_to_user_management'),
          cancelButtonText: translation('billing.common.cancel'),
        },
      )
        .then(() => {
          formJump('/account/user', 'GET');
          return Promise.reject();
        })
        .catch(() => Promise.reject());
    }
    return BillingMessageBox.alert(
      translation('billing.planform.zoom_meeting.less_than_assigned_number_tips_for_expire', args),
      translation('billing.cancel_title_sorry_to_see_you_go'),
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: translation('billing.got_it'),
      },
    ).then(() => {
      return Promise.reject();
    });
  }
  return Promise.resolve();
};

const checkForZePPA = async (ppaPlanCode) => {
  const { plan_code: planCode } = parseConsumptionCode(ppaPlanCode);
  // check ppa remaining credits, if switch will lose all remaining credits.
  const { result: remainingCredits } = await getPPARemainingCredits(planCode);
  if (remainingCredits > 0) {
    EventTracking.buyInteraction('order summary', `are you sure you want to place order? modal - impression`);
    return BillingMessageBox.confirm(
      translation('billing.zoom_events.upgrade.ppa_lose_prompt_desc', [
        translation('billing.planform.title.eventsaascore'),
      ]),
      translation('billing.zoom_events.upgrade.prompt_title'),
      {
        confirmButtonText: translation('billing.error.smb_plan_billing_cycle_change_yes'),
        cancelButtonText: translation('billing.error.smb_plan_billing_cycle_change_no'),
      },
    )
      .then(() => {
        EventTracking.buyInteraction('order summary', `are you sure you want to place order? modal - yes, place order`);
        return Promise.resolve();
      })
      .catch(() => {
        EventTracking.buyInteraction('order summary', `are you sure you want to place order? modal - no, go back`);
        return Promise.reject();
      });
  }
  return Promise.resolve();
};

const EventsSaasCapacityCheckResp = {
  CAN_SWITCH: 0,
  HAS_FREE_EVENT: 1,
  HAS_PAID_EVENT: -1,
  HAS_FREE_AND_PAID_EVENT: -3,
};

const saveDowngradeState = (oldZsPlan, newZePlan) => {
  const { assignLicenseQuantity = 0, planExpireTime } = _.get(store.getters.assignInfo, oldZsPlan.plan_code, {});
  const currentQuantity = _.get(newZePlan, 'cartInfo.selected_number', 0);
  let unAssignLicenseQuantity = assignLicenseQuantity - currentQuantity;
  if (unAssignLicenseQuantity > 0) {
    store.commit('savePersistState', {
      downgradeState: {
        updateType: updateTypeMap.downgrade,
        planExpireTime,
        unAssignLicenseQuantity,
        flowType: flowTypes.zs2ze,
        planName: translation('billing.planform.title.eventsaascore'),
      },
    });
  }
};

const checkForZeSaas = async (oldPlanCode) => {
  // reset persist state
  store.commit('savePersistState', { downgradeState: {} });

  const newZePlan = store.getters.getSelectedPlans[0];
  const oldZsPlan = store.getters.boughtPlans.find((bp) => bp.plan_code === oldPlanCode);
  const planExpireTime = dateTimeFormat(oldZsPlan?.cartInfo?.planExpireTime);
  const targetPlanCode = newZePlan.plan_code;
  const mrrStatus = checkMRSChange({ oldPlan: oldZsPlan, nextPlan: newZePlan });
  const isDowngrade = mrrStatus === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE;
  //check free/paid events
  const { status, result, errorMessage } = await checkZeSaasCapacity({
    srcPlanCode: oldPlanCode,
    targetPlanCode,
    isDowngrade,
  });
  if (status !== true) {
    return Promise.reject(errorMessage || 'System error, please try again later.');
  }

  let promptMsg = '';
  if (result === EventsSaasCapacityCheckResp.HAS_FREE_EVENT) {
    promptMsg = translation('billing.zoom_events.upgrade.saas_has_free_event_desc', [
      newZePlan.capacity,
      planExpireTime,
    ]);
  } else if (isDowngrade) {
    if (result === EventsSaasCapacityCheckResp.HAS_PAID_EVENT) {
      promptMsg = translation('billing.zoom_events.upgrade.saas_has_paid_event_desc', [
        newZePlan.capacity,
        planExpireTime,
      ]);
    } else if (result === EventsSaasCapacityCheckResp.HAS_FREE_AND_PAID_EVENT) {
      promptMsg = translation('billing.zoom_events.upgrade.saas_has_free_and_paid_event_desc', [
        newZePlan.capacity,
        planExpireTime,
      ]);
    }
    saveDowngradeState(oldZsPlan, newZePlan);
  }
  if (promptMsg) {
    EventTracking.buyInteraction('order summary', `are you sure you want to place order? modal - impression`);
    return BillingMessageBox.confirm(promptMsg, translation('billing.zoom_events.upgrade.prompt_title'), {
      dangerouslyUseHTMLString: true,
      confirmButtonText: translation('billing.error.smb_plan_billing_cycle_change_yes'),
      cancelButtonText: translation('billing.error.smb_plan_billing_cycle_change_no'),
    })
      .then(() => {
        EventTracking.buyInteraction('order summary', `are you sure you want to place order? modal - yes, place order`);
        return Promise.resolve();
      })
      .catch(() => {
        EventTracking.buyInteraction('order summary', `are you sure you want to place order? modal - no, go back`);
        return Promise.reject();
      });
  }

  return Promise.resolve();
};
const preCheckForZs2Ze = async () => {
  store.commit('togglePreCheckLoading', true);

  let { plan: planSubType, oldPlanCode } = pageConfig;
  if (ZOOM_EVENTS_SUB_TYPES.EVENT_SAAS_CORE_CONSUMPTION === planSubType) {
    return await checkForZePPA(oldPlanCode);
  } else if (ZOOM_EVENTS_SUB_TYPES.EVENT_SAAS_CORE === planSubType) {
    return await checkForZeSaas(oldPlanCode);
  }
  return Promise.reject();
};

const checkOutForZs2Ze = (submitParams) => {
  const { plan: planSubType, oldPlanCode } = pageConfig;
  const getters = store.getters;
  const oldPlan = getters.currentEditPlan;
  const newPlan = getters.getSelectedPlans[0];

  let planList = [];
  if (ZOOM_EVENTS_SUB_TYPES.EVENT_SAAS_CORE_CONSUMPTION === planSubType) {
    const { plan_code: ppaOldPlanCode } = parseConsumptionCode(oldPlanCode);
    const { plan_code: ppaNewPlanCode, attendees } = parseConsumptionCode(newPlan.plan_code);
    planList = [
      {
        quantity: attendees,
        planCode: ppaNewPlanCode,
        oldPlanCode: ppaOldPlanCode,
      },
    ];
  } else if (ZOOM_EVENTS_SUB_TYPES.EVENT_SAAS_CORE === planSubType) {
    planList = [
      {
        quantity: newPlan.cartInfo.selected_number,
        planCode: newPlan.plan_code,
        oldPlanCode: oldPlan?.plan_code,
      },
    ];
  }

  return updatePlansCheckout(
    submitParams,
    planList,
    UpdateTypes.ZS_TO_ZE,
    getters.orderDataPayload.coupon,
    null,
    false,
  );
};

const initShoppingCartForEduUpgrade = async (state) => {
  const { persist } = state;
  const { plan } = pageConfig?.editConfig || {}
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();
  plansof(boughtPlans).type(planTypes.ZOOM_MEETING).category(ZOOM_MEETING_SUB_TYPES.EDUCATION).get(0) || {};
  const eduPlan = plansof(boughtPlans).type(planTypes.ZOOM_MEETING).get(0) || {};
  const assignResult = await getAssignLicenseResult(eduPlan);
  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', (planList = []) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (plan === BASE_PLAN_SUB_TYPES.ZONE_EDU_SCH_CMP) {
      if (hasSelected) {
        return planList;
      }
      var selected_number = Math.max(ZOOM_MEETING_EDU_SCH_CMP_MIN_LICENSE,getEdu2EscUpgradePlanQuantity({
        boughtPlans,
        assignInfo: assignResult,
        category: ZOOM_MEETING_SUB_TYPES.EDUCATION,
      }))
      addToCartByCategoryAndBillingCycle(planList, getPlanTypeByCategory(plan), plan, BILLING_CYCLE.ANNUAL,selected_number);
      return planList;
    }
    if (plan === BASE_PLAN_SUB_TYPES.ZONE_EDU_PLUS) {
      if (hasSelected) {
        return planList;
      }
      const planInfo = getBundlePbxPlan({
        boughtPlans,
        planMapFlat: planList,
        assignInfo: assignResult,
        category: ZOOM_MEETING_SUB_TYPES.EDUCATION,
        planType: planTypes.ZONE_EDU_PLUS,
      });
      return planList.map((plan) => {
        let selected_number = planInfo[plan.type]?.planCode === plan.plan_code ? planInfo[plan.type].quantity : 0;
        if (planInfo[plan.type]?.planCode === plan.plan_code && plan.type === planTypes.ZONE_EDU_PLUS) {
          selected_number = Math.max(ZOOM_MEETING_EDU_PLUS_MIN_LICENSE, selected_number);
        }
        return {
          ...plan,
          cartInfo: {
            ...plan.cartInfo,
            selected_number: selected_number > 0 ? selected_number : plan.cartInfo.selected_number,
          },
        };
      });
    }

    return planList;
  });
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
};

async function initShoppingCartForEduSch2EduSchPlus(state) {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();

  plansof(boughtPlans).type(planTypes.ZOOM_MEETING).category(ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP).get(0) || {};
  const eduPlan = plansof(boughtPlans).type(planTypes.ZOOM_MEETING).get(0) || {};
  const assignResult = await getAssignLicenseResult(eduPlan);

  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate(
    'loadDefaultPlansByPreloadCouponForUpgrade',
    loadDefaultPlansByPreloadCouponForUpgrade(boughtPlans, eduPlan),
  );
  loader.decorate('loadDefaultSelectByQuery', (planList = []) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (!hasSelected) {
      const planInfo = getBundlePbxPlan({
        boughtPlans,
        planMapFlat: planList,
        assignInfo: assignResult,
        category: ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP,
        planType: planTypes.ZONE_EDU_PLUS,
      });

      return planList.map((plan) => {
        let selected_number = planInfo[plan.type]?.planCode === plan.plan_code ? planInfo[plan.type].quantity : 0;
        if (planInfo[plan.type]?.planCode === plan.plan_code && plan.type === planTypes.ZONE_EDU_PLUS) {
          selected_number = Math.max(ZOOM_MEETING_EDU_PLUS_MIN_LICENSE, selected_number);
        }
        return {
          ...plan,
          cartInfo: {
            ...plan.cartInfo,
            selected_number: selected_number > 0 ? selected_number : plan.cartInfo.selected_number,
          },
        };
      });
    }

    return planList;
  });
  const planList = await loader.dispatch();

  return {
    allPlanMap,
    assignResult,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  };
}

function checkOutForEduSch2EduSchPlusSubmit(submitParams) {
  const { orderDataPayload } = store.getters;
  const ret = getEdu2EduPlusUpdateForm(store.getters);
  return updatePlansCheckout(submitParams, ret.planList, ret.actionType, orderDataPayload.coupon);
}

function checkOutForEduToEscSubmit(submitParams) {
  const { orderDataPayload } = store.getters;
  const ret = getEdu2EscUpdateForm(store.getters);
  return updatePlansCheckout(submitParams, ret.planList, ret.actionType, orderDataPayload.coupon);
}


export default {
  [flowTypes.upgrade]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForUpgrade(...arguments);
    }
    submit(submitParams) {
      return checkOutForUpgrade(submitParams);
    }
  },
  [flowTypes.pro2biz]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForPro2Biz(...arguments);
    }
    beforeSubmit() {
      return preCheckForUpgrade();
    }
    submit(submitParams) {
      return checkOutForPro2BizSubmit(submitParams);
    }
  },
  [flowTypes.trial2paid]: class extends Flow {
    view() {
      return pageConfig.hasBillingZuoraId ? VIEWS.COMPOUND : VIEWS.CONFIG;
    }
    init() {
      return initShoppingCartTrial2Paid(...arguments);
    }
    beforeSubmit() {
      return checkAssignLicenseForFree2Paid();
    }
    submit(submitParams) {
      return checkOutForTrial2Paid(submitParams);
    }
  },
  [flowTypes.pro2bundle]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForMeeting2Bundle(...arguments);
    }
    beforeSubmit() {
      return preCheckForUpgrade();
    }
    submit(submitParams) {
      return checkOutForMeeting2BundleSubmit(submitParams);
    }
  },
  [flowTypes.biz2bundle]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForMeeting2Bundle(...arguments);
    }
    beforeSubmit() {
      return preCheckForUpgrade();
    }
    submit(submitParams) {
      return checkOutForMeeting2BundleSubmit(submitParams);
    }
  },
  [flowTypes.pro2prop]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForPro2Prop(...arguments);
    }
    beforeSubmit() {
      return preCheckForUpgrade();
    }
    submit(submitParams) {
      return checkOutForPro2PropSubmit(submitParams);
    }
  },
  [flowTypes.prop2bundle]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForProp2Bundle(...arguments);
    }
    beforeSubmit() {
      return preCheckForUpgrade();
    }
    submit(submitParams) {
      return checkOutForProp2BundleSubmit(submitParams);
    }
  },
  [flowTypes.zs2ze]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForZs2Ze(...arguments);
    }
    beforeSubmit() {
      return preCheckForZs2Ze(...arguments);
    }

    submit(submitParams) {
      return checkOutForZs2Ze(submitParams);
    }

    afterSubmitShowMessage(msgInstance) {
      if (msgInstance?.message) {
        EventTracking.buyInteraction('banner - product level', `${msgInstance?.message} - impression`);
        msgInstance.onClose = () => {
          EventTracking.buyInteraction('banner - product level', `${msgInstance?.message} - close`);
        };
      }
    }
  },
  [flowTypes.esc2escp]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForEduSch2EduSchPlus(...arguments);
    }
    beforeSubmit() {
      return preCheckForUpgrade();
    }
    submit(submitParams) {
      return checkOutForEduSch2EduSchPlusSubmit(submitParams);
    }
  },
  [flowTypes.edu2esc]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      return initShoppingCartForEduUpgrade(...arguments);
    }
    beforeSubmit() {
      return preCheckForUpgrade();
    }
    submit(submitParams) {
      return checkOutForEduToEscSubmit(submitParams);
    }
  },
  [flowTypes.pbx2prop]: Pbx2prop,
};
